/**
 * Section: Services & Upgrades — featuredActions. An add/remove/reorder list of
 * { label, url } rows, all [TEMPLATE]. featuredActions only render on the generic
 * style; the server validatePassDesign emits a non-blocking WARN
 * (path: 'featuredActions', severity: 'warn') when the list is non-empty and
 * style !== 'generic'. This editor renders that warn inline (amber, never blocks).
 * Per-row findings (path: 'featuredActions[i].url' — the https-only scheme ERROR)
 * render under their row's Link input — red for error, amber for warn (same
 * severity styling as the sibling BarcodeEditor/ColorsEditor sections).
 */
import type { DeepPartial, PassDesign, PassDesignError } from '@insurwiz/wallet-core';

import { HelpHint, HelpText } from '../designer/HelpHint';

type FeaturedAction = { label: string; url: string };

export interface FeaturedActionsEditorProps {
  design: PassDesign;
  errors: PassDesignError[];
  onChange: (patch: DeepPartial<PassDesign>) => void;
}

export function FeaturedActionsEditor({
  design,
  errors,
  onChange,
}: FeaturedActionsEditorProps): JSX.Element {
  const actions: FeaturedAction[] = design.featuredActions ?? [];
  const actionErrors = errors.filter((e) => e.path === 'featuredActions');

  function write(next: FeaturedAction[]): void {
    onChange({ featuredActions: next });
  }
  function add(): void {
    write([...actions, { label: '', url: '' }]);
  }
  function remove(index: number): void {
    write(actions.filter((_, i) => i !== index));
  }
  function patch(index: number, p: Partial<FeaturedAction>): void {
    write(actions.map((a, i) => (i === index ? { ...a, ...p } : a)));
  }

  return (
    <section className="card p-4 space-y-3" aria-label="Kiemelt gombok">
      <h3 className="text-sm font-semibold text-slate-800">Kiemelt gombok</h3>
      <HelpText>
        Nagy gombok a pass alján — pl. &apos;Utazásbiztosítás&apos; vagy
        &apos;Ügyfélszolgálat&apos;. Csak az »általános« stílusú passzon jelennek meg.
      </HelpText>

      {actionErrors.map((e, i) => (
        <p
          key={`fa-${String(i)}`}
          className={e.severity === 'error' ? 'text-xs text-red-600' : 'text-xs text-amber-700'}
        >
          {e.message}
        </p>
      ))}

      {actions.map((action, index) => {
        const urlNotes = errors.filter((e) => e.path === `featuredActions[${String(index)}].url`);
        return (
          // min-w-0 on both input cells (and the inputs): a grid item's default
          // min-width is its content's intrinsic min-width, so the inputs'
          // native size would overflow a narrow (~380px) host column —
          // min-w-0 lets the fr tracks actually shrink.
          <div
            key={`action-${String(index)}`}
            className="grid grid-cols-[2fr_3fr_auto] gap-2 items-end"
          >
            {/* div (not label): a <label> would associate the HelpHint button too and
                break unique getByLabelText matches; the input carries its own aria-label */}
            <div className="flex flex-col gap-0.5 text-xs text-slate-600 min-w-0">
              <span className="flex items-center gap-1">
                Címke
                <HelpHint text="Ez a szöveg jelenik meg a gombon." label="Címke súgó" />
              </span>
              <input
                aria-label="Címke"
                type="text"
                className="input min-w-0"
                placeholder="Ügyfélszolgálat"
                value={action.label}
                onChange={(e) => patch(index, { label: e.target.value })}
              />
            </div>
            <label className="flex flex-col gap-0.5 text-xs text-slate-600 min-w-0">
              Link
              <input
                aria-label="Link"
                type="url"
                className="input min-w-0"
                placeholder="https://insurwiz.io/kapcsolat"
                value={action.url}
                onChange={(e) => patch(index, { url: e.target.value })}
              />
              {urlNotes.map((e, i) => (
                <p
                  key={`${e.path}-${String(i)}`}
                  className={`text-xs ${e.severity === 'error' ? 'text-red-600' : 'text-amber-700'}`}
                >
                  {e.message}
                </p>
              ))}
            </label>
            <button
              type="button"
              aria-label={`Gomb törlése ${String(index)}`}
              className="text-xs text-red-600"
              onClick={() => remove(index)}
            >
              ✕
            </button>
          </div>
        );
      })}

      <button type="button" className="btn-secondary text-xs" onClick={add}>
        + Művelet
      </button>
    </section>
  );
}
